<ng-container>
    <h2 mat-dialog-title class="lmc-dialog-title">Preview Transactions</h2>

    <mat-dialog-content class="preview-content">

        <!-- File Information Card -->
        <mat-card class="content-card info-card">
            <mat-card-content>
                <p class="section-group-label">
                    <mat-icon>info_outline</mat-icon>
                    File Information
                </p>
                <div class="details-grid">
                    <div class="details-item">
                        <span class="details-label">Customer Number</span>
                        <span class="details-value">{{ parsedFile.header.customerNumber }}</span>
                    </div>
                    <div class="details-item">
                        <span class="details-label">Total Transactions</span>
                        <span class="details-value">{{ parsedFile.transactions.length }}</span>
                    </div>
                    <div class="details-item">
                        <span class="details-label">Total Debit Amount</span>
                        <span class="details-value">{{ formatAmount(totalDebitAmount) }}</span>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- Paid-off loans (skipped, never submitted) — informational, so collapsed by default -->
        <mat-accordion class="paid-off-section" *ngIf="paidOffResults.length > 0">
            <mat-expansion-panel [(expanded)]="isPaidOffExpanded">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        {{ paidOffResults.length }} Paid-off loan{{ paidOffResults.length > 1 ? 's' : '' }}
                    </mat-panel-title>
                </mat-expansion-panel-header>

                <!-- Rendered only once the panel is opened. -->
                <ng-template matExpansionPanelContent>
                    <!-- The header already carries the count; this only states the outcome. -->
                    <p class="skip-text">These transactions will not be submitted to KapCharge.</p>
                    <div class="tx-list">
                        <ng-container *ngTemplateOutlet="listHeader; context: { readonly: true }"></ng-container>
                        <ng-container *ngFor="let row of paidOffResults">
                            <ng-container *ngTemplateOutlet="transactionCard; context: { row: row, readonly: true }"></ng-container>
                        </ng-container>
                    </div>
                </ng-template>
            </mat-expansion-panel>
        </mat-accordion>

        <!--
            Corrections card — the same identity + actions blocks a transaction card is built
            from, and nothing else: it explains the held-back rows, the cards below are where
            the detail lives. Its text is static in both toggle states so the layout never
            shifts.
        -->
        <mat-card appearance="outlined" class="corrections-card" *ngIf="blockingResults.length > 0">
            <div class="corrections-card-body">

                <div class="cc-identity">
                    <mat-icon class="cc-icon">pause_circle_outline</mat-icon>
                    <div class="cc-identity-text">
                        <span class="cc-title">
                            {{ blockingResults.length }} transaction{{ blockingResults.length > 1 ? 's need' : ' needs' }} correction
                        </span>
                        <span class="cc-subtitle">
                            Fix the bank details here, or correct the customer's information in NLS.
                        </span>
                    </div>
                </div>

                <div class="cc-actions">
                    <!-- Spreadsheet of the held-back rows, for correcting the customer data in NLS. -->
                    <button mat-stroked-button class="cc-export" type="button"
                            (click)="onExportCorrections()"
                            matTooltip="Download the transactions that need correction as an Excel file">
                        <mat-icon>file_download</mat-icon>
                        <span>Export</span>
                    </button>

                    <!-- Submit the file without correcting these rows; the hint under the
                         toggle states what that costs, so it is read before it is flipped. -->
                    <div class="cc-skip">
                        <mat-slide-toggle color="primary"
                                          [checked]="skipCorrections"
                                          (change)="skipCorrections = $event.checked">
                            Skip corrections
                        </mat-slide-toggle>
                        <span class="cc-skip-hint">Saved as Invalid, never sent to KapCharge</span>
                    </div>
                </div>

            </div>
        </mat-card>

        <!-- Transactions: one card per row, laid out like the batch cards on the list page -->
        <div class="list-section" *ngIf="previewRows.length > 0">
            <h3 class="section-title">Transactions</h3>

            <div class="tx-list">
                <ng-container *ngTemplateOutlet="listHeader; context: { readonly: false }"></ng-container>
                <ng-container *ngFor="let row of pagedRows; trackBy: trackByRow">
                    <ng-container *ngTemplateOutlet="transactionCard; context: { row: row, readonly: false }"></ng-container>
                </ng-container>
            </div>

            <mat-paginator
                    [length]="previewRows.length"
                    [pageIndex]="pageIndex"
                    [pageSize]="pageSize"
                    [pageSizeOptions]="[10, 25, 50, 100]"
                    showFirstLastButtons
                    (page)="onPageChange($event)"
            ></mat-paginator>
        </div>


        <!--
            Column titles for the card list. One strip above the list rather than a label on
            every card: the titles are the same on all of them, and repeating them per row is
            exactly what made the earlier design tall. Its blocks carry the same widths as a
            card's, so a title sits over the value it names.
        -->
        <ng-template #listHeader let-readonly="readonly">
            <div class="tx-list-header" aria-hidden="true">
                <span class="th-status">Status</span>
                <span class="th-who">Customer / Loan #</span>
                <span class="th-due">Due Date</span>
                <span class="th-bank">Bank Account</span>
                <span class="th-amount">Amount</span>
                <span class="th-actions" *ngIf="!readonly"></span>
            </div>
        </ng-template>

        <!--
            One transaction, as a card. Every value sits on a single line — the blocks and
            idioms of a batch card (fixed-width status badge, date chip, right-aligned
            figure), compacted to one line because a transaction carries far fewer facts
            than a batch does. The note is the only thing that takes a line of its own, and
            only when there is one.
        -->
        <ng-template #transactionCard let-row="row" let-readonly="readonly">
            <mat-card appearance="outlined" class="tx-card"
                      [class.is-blocking]="!readonly && isBlocking(row.status)"
                      [class.is-manual-match]="!readonly && row.status === TransactionPreviewStatus.ManualMatch"
                      [class.is-readonly]="readonly">
                <div class="tx-card-body">

                    <span class="status-badge tc-status" [style.--status-color]="statusColor(row.status)">
                        {{ statusLabel(row.status) }}
                    </span>

                    <!-- Who the money is for: the customer, then the loan it was matched to -->
                    <div class="tc-who">
                        <span class="tc-name">{{ row.customerName || row.recipientName }}</span>
                        <span class="tc-loan" *ngIf="row.loanNumber; else noLoan">{{ row.loanNumber }}</span>
                        <ng-template #noLoan><span class="tc-loan is-missing">No loan matched</span></ng-template>
                    </div>

                    <span class="tc-due"><span class="date-chip">{{ row.dueDate }}</span></span>

                    <span class="tc-bank"
                          [matTooltip]="row.branchName"
                          [matTooltipDisabled]="!row.branchName">
                        {{ row.institutionCode }}-{{ row.transitNumber }}
                        <span class="account-number">{{ row.accountNumber }}</span>
                    </span>

                    <span class="tc-amount">{{ formatAmount(row.amount) }}</span>

                    <!-- Edit is the card's only action; paid-off rows are never submitted. -->
                    <div class="tc-actions" *ngIf="!readonly">
                        <button mat-icon-button type="button"
                                (click)="onEditTransaction(row)"
                                matTooltip="Edit bank details">
                            <mat-icon>edit</mat-icon>
                        </button>
                    </div>

                    <!-- Note: its own line, so the reason wraps as prose rather than in a cell -->
                    <div class="tc-note" *ngIf="row.error">
                        <mat-icon>info_outline</mat-icon>
                        <span>{{ row.error }}</span>
                    </div>

                </div>
            </mat-card>
        </ng-template>

    </mat-dialog-content>

    <mat-dialog-actions align="end">
        <button mat-stroked-button (click)="onCancel()" type="button" [disabled]="isSubmitting">
            Cancel
        </button>
        <!-- Disabled while any row still needs correcting; the notice above says why. -->
        <button mat-raised-button color="primary" (click)="onSubmit()" type="button"
                [disabled]="!canSubmit">
            <mat-spinner *ngIf="isSubmitting" diameter="20" class="btn-spinner"></mat-spinner>
            <span>Submit</span>
        </button>
    </mat-dialog-actions>
</ng-container>